import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useFeature } from '../../../context/AuthContext';
import useAdminAccess from '../../../context/useAdminAccess';
import { ADMIN_NAV } from './adminNav';
import './adminShell.css';

const COLLAPSE_KEY = 'rtg.adminSidebarCollapsed';
const readCollapsed = () => {
  try { return window.localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
};

// Admin layout chrome: a vertical brand sidebar (reference-style nav pills) +
// a top bar with the page title, an optional search/actions slot, and the
// admin identity + an easy "Exit to app" route back to the user view.
//
// Tab state lives on the /admin #tab= hash (owned by AdminDashboard). The shell
// is presentational: it receives the active key, an onSelect callback, and a
// counts map for nav badges. Standalone admin pages (e.g. /admin/apps/:id) pass
// an `activeKey` to light up the right nav item and use plain links instead.
export default function AdminShell({
  activeKey,                // which nav item to highlight (matches /admin/<key>)
  counts = {},              // { tools: n, apps: n } badge counts
  title,
  subtitle,
  actions,                  // right-aligned topbar slot (search, buttons)
  children,
}) {
  const { user } = useAuth();
  const rbacOn = useFeature('rbac_management');
  const vendorsOn = useFeature('vendor_management');
  const itAppsOn = useFeature('it_apps');
  const { isFullAdmin, areas } = useAdminAccess();
  const navItems = ADMIN_NAV.filter((item) => {
    if (item.key === 'roles' && !rbacOn) return false;
    if (item.key === 'vendors' && !vendorsOn) return false;
    if (item.key === 'it_apps' && !itAppsOn) return false;
    return isFullAdmin || areas.includes(item.key);
  });
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try { window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return (
    <div className={`adm-shell${collapsed ? ' is-collapsed' : ''}`}>
      <aside className="adm-side" aria-label="Admin navigation">
        <Link to="/" className="adm-brand" title="Back to app">
          <img src="/images/rtg-logo-red.png" alt="" className="adm-brand-logo" />
          <span className="adm-brand-text">
            <span className="adm-brand-name">Nexus</span>
            <span className="adm-brand-sub">Admin</span>
          </span>
        </Link>

        <nav className="adm-nav">
          {navItems.map((item) => {
            const isActive = item.key === activeKey;
            const badge = item.badge ? counts[item.badge] : 0;
            const inner = (
              <>
                <span className="adm-nav-icon" aria-hidden="true">{item.icon}</span>
                <span className="adm-nav-label">{item.label}</span>
                {badge > 0 && (
                  <span className="adm-nav-badge" aria-label={`${badge} pending`}>{badge}</span>
                )}
              </>
            );
            const cls = `adm-nav-item${isActive ? ' is-active' : ''}`;
            // Every nav item is a real route (/admin/<key>) so links are
            // shareable and open-in-new-tab works.
            return (
              <Link
                key={item.key}
                to={`/admin/${item.key}`}
                className={cls}
                aria-current={isActive ? 'page' : undefined}
              >
                {inner}
              </Link>
            );
          })}
        </nav>

        <button
          type="button"
          className="adm-exit adm-collapse-side"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={collapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {collapsed
              ? <path d="m9 18 6-6-6-6" />
              : <path d="m15 18-6-6 6-6" />}
          </svg>
          <span>{collapsed ? 'Expand' : 'Collapse'}</span>
        </button>
      </aside>

      <div className="adm-body">
        <header className="adm-topbar">
          <div className="adm-topbar-head">
            {title && <h1 className="adm-topbar-title">{title}</h1>}
            {subtitle && <p className="adm-topbar-sub">{subtitle}</p>}
          </div>
          <div className="adm-topbar-right">
            {actions && <div className="adm-topbar-actions">{actions}</div>}
            <Link to="/" className="adm-topbar-exit" title="Switch to user view" aria-label="User view">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m16 17 5-5-5-5" /><path d="M21 12H9" /><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              </svg>
              <span>User view</span>
            </Link>
            <div className="adm-user" title={user?.email || ''}>
              {user?.photo_url
                ? <img className="adm-user-avatar" src={user.photo_url} alt="" referrerPolicy="no-referrer" />
                : <span className="adm-user-avatar adm-user-avatar--fallback" aria-hidden="true">{(user?.name || '?').charAt(0).toUpperCase()}</span>}
              <span className="adm-user-meta">
                <span className="adm-user-name">{user?.name || 'Admin'}</span>
                <span className="adm-user-role">{isFullAdmin ? 'Admin' : 'Admin access'}</span>
              </span>
            </div>
          </div>
        </header>

        <div className="adm-content">{children}</div>
      </div>
    </div>
  );
}
